import { Router } from 'aurelia-router';
import * as moment from 'moment';
import { I18N } from 'aurelia-i18n';
import { Customer } from '../../models/customer';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { PortalUtility } from '../../helpers/utility';
import { Business } from '../../models/business';
import { PhoneFormat } from '../../helpers/phone-format';
import { EventAggregator } from 'aurelia-event-aggregator';
import { mock, instance, verify, when, anything, spy } from 'ts-mockito';
import { EZPay } from './ezpay';
import { ValidationControllerFactory } from 'aurelia-validation';
import { TemplateService } from '../../services/template-service';
import { DialogService } from 'aurelia-dialog';
import { ApplePaySettings, ApplePayUtil } from '../../models/digital-wallet';
import { Merchant } from '../../models/merchant';

describe('NewPortalWeb', () => {
	describe('pages', () => {
		describe('ezpay', () => {
			let component: EZPay;
			const mockCustomer: Customer = mock(Customer);
			const mockRouter: Router = mock(Router);
			const mockPortalUtility: PortalUtility = mock(PortalUtility);
			let stubState: State;
			const mockPayrazrService: PayrazrService = mock(PayrazrService);
			const mockTemplateService: TemplateService = mock(TemplateService);
			const mockI18N: I18N = mock(I18N);
			const mockEventAggregator: EventAggregator = mock(EventAggregator);
			const mockBusiness: Business = mock(Business);
			const mockPhoneFormatter: PhoneFormat = mock(PhoneFormat);
			const mockValidationControllerFactory: ValidationControllerFactory = mock(ValidationControllerFactory);
			const mockDialogService: DialogService = mock(DialogService);
			const mockMerchant: Merchant = mock(Merchant);

			beforeEach(() => {
				stubState = new State(
					instance(mockI18N),
					instance(mockEventAggregator)
				);

				stubState.customer = instance(mockCustomer);
				stubState.business = instance(mockBusiness);
				const i18next = {
					t: (a, b) => a
				};
				when(mockI18N.i18next).thenReturn(i18next);
				spyOn(i18next, 't').and.returnValue('localized');

				component = new EZPay(
					instance(mockPayrazrService),
					instance(mockTemplateService),
					stubState,
					instance(mockRouter),
					instance(mockPortalUtility),
					instance(mockI18N),
					instance(mockPhoneFormatter),
					instance(mockValidationControllerFactory),
					instance(mockDialogService),
					instance(mockEventAggregator),
				);
			});

			it('should initialize EZPay component', () => {
				expect(component).toBeTruthy();
			});

			describe('defaultEzPayFields', () => {
				it('should set global state ezpay registrationId, registrationId2, registrationId3 required to false if value is undefined and zipcode required to true if it is undefined', () => {
					stubState.business = {
						portalSettings: {
							ezpay: {
								registrationIdSettings: undefined,
								registrationId2Settings: undefined,
								registrationId3Settings: undefined,
								zipcodeSettings: undefined
							}
						}
					};
					component.defaultEzPayFields();

					expect(stubState.business.portalSettings.ezpay.registrationIdSettings).toEqual({ required: false });
					expect(stubState.business.portalSettings.ezpay.registrationId2Settings).toEqual({ required: false });
					expect(stubState.business.portalSettings.ezpay.registrationId3Settings).toEqual({ required: false });
					expect(stubState.business.portalSettings.ezpay.zipcodeSettings).toEqual({ required: true });
				});
			});

			describe('start', () => {
				it('should set hideEzpayTerms of state to false and page state to empty object', () => {
					stubState.business = {
						portalSettings: {
							businessSettings: {
								hideTerms: undefined,
								hideDisclosures: false
							}
						}
					};
					component.displayEzpayAgreeTermsError = false;
					let element = document.createElement('input') as HTMLInputElement;
					element.setAttribute('id', 'ezpay-t-c-agreed');
					element.checked = false;
					spyOn(document, 'getElementById').and.returnValue(element);
					component.start();
					expect(stubState.hideEzpayTerms).toEqual(false);
					expect(component.displayEzpayAgreeTermsError).toEqual(true);
				});
				it('should set hideEzpayTerms of state to true when checked "ezpay-t-c-agreed" checkbox', () => {
					stubState.business = {
						portalSettings: {
							businessSettings: {
								hideTerms: false,
								hideDisclosures: false
							}
						}
					};
					let element = document.createElement('input') as HTMLInputElement;
					element.setAttribute('id', 'ezpay-t-c-agreed');
					element.checked = true;
					spyOn(document, 'getElementById').and.returnValue(element);
					component.start();
					expect(stubState.hideEzpayTerms).toEqual(true);
					expect(component.pageState).toEqual({});
				});
				it('should set hideEzpayTerms of state to true and component doContinue func called', () => {
					stubState.business = {
						portalSettings: {
							businessSettings: {
								hideTerms: true,
								hideDisclosures: true
							}
						}
					};
					const spy = spyOn(component, 'doContinue').and.stub();
					let element = document.createElement('input') as HTMLInputElement;
					element.setAttribute('id', 'ezpay-t-c-agreed');
					element.checked = true;
					spyOn(document, 'getElementById').and.returnValue(element);
					component.start();
					expect(stubState.hideEzpayTerms).toEqual(true);
					expect(spy).toHaveBeenCalled();
				});
			});
			describe('activate()', () => {
				it('should call component defaultEzPayFields, getPageStartState and requireAccountLookup must be true ', () => {
					spyOn(component, 'defaultEzPayFields');
					spyOn(component, 'getPageStartState');
					stubState.business = {
						portalSettings: {
							ezpay: {
								ezpayOnly: undefined,
								displayAccountBalanceOnPaymentForm: undefined,
								accountNumberHandling: 'Lookup'
							},
							fields: {
								paymentDate: '',
								paymentAmount: ''
							},
							bannerAlignment: ''
						}
					};
					component.activate();
					expect(component.defaultEzPayFields).toHaveBeenCalled();
					expect(component.getPageStartState).toHaveBeenCalled();
					expect(component.requireAccountLookup).toBeTrue();
				});
			});
			describe('attatched()', () => {
				it('should call utility scrollToTop', () => {
					component.pageState = component.pageStates.start;
					spyOn(component, 'showBannerImage');
					component.attached();
					verify(mockPortalUtility.scrollToTop()).called();
					expect(component.showBannerImage).toHaveBeenCalledTimes(0);
				});

				it('should call utility scrollToTop and call showBannerImage when pageState is not start', () => {
					component.pageState = component.pageStates.disclosures;
					spyOn(component, 'showBannerImage');
					component.attached();
					verify(mockPortalUtility.scrollToTop()).called();
					expect(component.showBannerImage).toHaveBeenCalled();
				});
			});
			describe('showBannerImage', () => {
				it('should set canShowBanner as true and hidden attribute should not be present on img html element with footer-logo as id ', () => {
					component.canShowBanner = false;
					let element = document.createElement('img') as HTMLImageElement;
					element.setAttribute('id', 'footer-logo');
					spyOn(document, 'getElementById').and.returnValue(element);
					component.showBannerImage();
					expect(component.canShowBanner).toBeTrue();
					expect(element.hasAttribute("hidden")).toBeFalse();
				});
				it('should set canShowBanner as false and hidden attribute should be present on img html element with footer-logo as id ', () => {
					component.canShowBanner = false;
					let element = document.createElement('img') as HTMLImageElement;
					element.setAttribute('id', 'footer-logo');
					spyOn(document, 'getElementById').and.returnValue(element);
					component.showBannerImage(false);
					expect(component.canShowBanner).toBeFalse();
					expect(element.hasAttribute("hidden")).toBeTrue();
				});
			});
			describe('getPageStartState', () => {
				it('should return pageState.start value when portalsettings ezpayOnly and ezpayOnlyStartButton selected', () => {
					component.pageStates = {
						start:  true
					};
					stubState.business = {
						portalSettings: {
							ezpay: {
								ezpayOnly: true,
								ezpayOnlyStartButton: true
							}
						}
					};
					let returnValue = component.getPageStartState();
					expect(returnValue).toBe(true);
				});
				it('should return pageStates.terms when portalsettings ezpayOnly and ezpayOnlyStartButton not selected but hideTerms selected', () => {
					component.pageStates = {
						terms: true
					};
					stubState.business = {
						portalSettings: {
							ezpay: {
								ezpayOnly: false,
								ezpayOnlyStartButton: false
							},
							businessSettings: {
								hideTerms: false,
								hideTermsAck: false
							}
						}
					};
					let returnValue = component.getPageStartState();
					expect(returnValue).toBe(true);
				});
				it('should return pageStates.disclosures when portalsettings ezpayOnly and ezpayOnlyStartButton not selected, hideTerms selected but hideDisclosures selected', () => {
					component.pageStates = {
						disclosures: true
					};
					stubState.business = {
						portalSettings: {
							ezpay: {
								ezpayOnly: false,
								ezpayOnlyStartButton: false
							},
							businessSettings: {
								hideTerms: true,
								hideDisclosures: false,
								hideTermsAck: false
							}
						}
					};
					let returnValue = component.getPageStartState();
					expect(returnValue).toBe(true);
				});
				it('should return pageStates.payments and call component docontinue when portalsettings ezpayOnly and ezpayOnlyStartButton, terms, hideDisclosures not selected ', () => {
					const spy = spyOn(component, 'doContinue').and.stub();
					component.pageStates = {
						payment: true
					};
					stubState.business = {
						portalSettings: {
							ezpay: {
								ezpayOnly: false,
								ezpayOnlyStartButton: false
							},
							businessSettings: {
								hideTerms: true,
								hideDisclosures: true,
								hideTermsAck:false
							}
						}
					};
					let returnValue = component.getPageStartState();
					expect(spy).toHaveBeenCalled();
					expect(returnValue).toBe(true);
				});
				it('should return pageStates.disclosures when portalsettings ezpayOnly and ezpayOnlyStartButton not selected, hideTermsAck selected but hideDisclosures not selected', () => {
					component.pageStates = {
						disclosures: true
					};
					stubState.business = {
						portalSettings: {
							ezpay: {
								ezpayOnly: false,
								ezpayOnlyStartButton: false
							},
							businessSettings: {
								hideTerms: false,
								hideDisclosures: false,
								hideTermsAck: true
							}
						}
					};
					let returnValue = component.getPageStartState();
					expect(returnValue).toBe(true);
				});

				it('should return pageStates.payments and call component docontinue when portalsettings ezpayOnly and ezpayOnlyStartButton, terms, hideDisclosures not selected but terms ack is selected', () => {
					const spy = spyOn(component, 'doContinue').and.stub();
					component.pageStates = {
						payment: true
					};
					stubState.business = {
						portalSettings: {
							ezpay: {
								ezpayOnly: false,
								ezpayOnlyStartButton: false
							},
							businessSettings: {
								hideTerms: false,
								hideDisclosures: true,
								hideTermsAck: true
							}
						}
					};
					let returnValue = component.getPageStartState();
					expect(spy).toHaveBeenCalled();
					expect(returnValue).toBe(true);
				});
			});
			describe('termsAgreed', () => {
				it('should set pageState to payment and call component doContinue, after terms agreed if portal business settings hideDisclosure is true', () => {
					const spy = spyOn(component, 'doContinue').and.stub();
					spyOn(component, 'showBannerImage').and.stub();
					component.pageStates = {
						payment: true
					};
					stubState.business = {
						portalSettings: {
							businessSettings: {
								hideDisclosures: true
							}
						}
					};
					component.termsAgreed();
					expect(component.pageState).toEqual(true);
					expect(spy).toHaveBeenCalled();
					expect(component.showBannerImage).toHaveBeenCalled();
				});
				it('should set pageState to disclosure, after terms agreed if portal business settings hideDisclosure is false', () => {
					spyOn(component, 'showBannerImage').and.stub();
					component.pageStates = {
						disclosures: true
					};
					stubState.business = {
						portalSettings: {
							businessSettings: {
								hideDisclosures: false
							}
						}
					};
					component.termsAgreed();
					expect(component.pageState).toEqual(true);
					expect(component.showBannerImage).toHaveBeenCalled();
				});
			});
			describe('disclosuresAgreed', () => {
				it('should set pageState to payment and call component doContinue, after disclosures agreed', () => {
					const spy = spyOn(component, 'doContinue').and.stub();
					component.pageStates = {
						payment: true
					};
					component.disclosuresAgreed();
					expect(component.pageState).toEqual(true);
					expect(spy).toHaveBeenCalled();
				});
			});
			describe('reset', () => {
				it('should clear validation errors and set it to new array', () => {
					component.validationErrors = ['test error'];
					component.reset();
					expect(component.validationErrors).toEqual([]);
				});
			});
			describe('returnToEzpayForm', () => {
				it('should set movingToConfirmationPage to flase and pageState to payment', () => {
					component.pageStates = {
						payment: true
					};
					component.returnToEzpayForm();
					expect(component.movingToConfirmationPage).toEqual(false);
					expect(component.pageState).toEqual(true);
				});
			});
			describe('returnToLogin', () => {
				it('should navigate router to logout if portalSettings ezpayOnly is true', () => {
					stubState.business = {
						portalSettings: {
							ezpay: {
								ezpayOnly: true
							}
						}
					};
					component.returnToLogin();
					verify(mockRouter.navigateToRoute('logout')).called();
				});
				it('should navigate router to login if portalSettings ezpayOnly is not true', () => {
					stubState.business = {
						portalSettings: {
							ezpay: {
								ezpayOnly: false
							}
						}
					};
					component.returnToLogin();
					verify(mockRouter.navigateToRoute('login')).called();
				});
			});
			describe('applyPortfolioSettingsToPage', () => {
				it('component maxFuture should set to 15 when portal settings paymentTypes maxFuture is set', () => {
					component.model.paymentDate = '';
					stubState.business = {
						portalSettings: {
							paymentTypes: {
								maxFutureDays: 15
							},
							ezpay: {
								paymentTypes: {
									allowFuture: true,
									allowSingle: true
								},
								accountNumberHandling: ''
							},
							businessSettings: {
								hideTerms: true,
								hideDisclosures: true
							}
						}
					};
					component.applyPortfolioSettingsToPage();
					expect(component.maxFuture).toEqual(15);
				});
				it('component maxFuture should set to 1 when portal settings paymentTypes maxFuture is not set', () => {
					component.model.paymentDate = '';
					stubState.business = {
						portalSettings: {
							paymentTypes: {
								maxFutureDays: ''
							},
							ezpay: {
								paymentTypes: {
									allowFuture: true,
									allowSingle: true
								},
								accountNumberHandling: ''
							},
							businessSettings: {
								hideTerms: true,
								hideDisclosures: true
							}
						}
					};
					component.applyPortfolioSettingsToPage();
					expect(component.maxFuture).toEqual(1);
				});
				it('component allowFuturePayment and allowPayToday should set to portalSettings ezpay paymentTypes values when ezpay paymentTypes set and accountHandling is not lookup', () => {
					component.model.paymentDate = '';
					stubState.business = {
						portalSettings: {
							paymentTypes: {
								maxFutureDays: ''
							},
							ezpay: {
								paymentTypes: {
									allowFuture: true,
									allowSingle: true
								},
								accountNumberHandling: ''
							},
							businessSettings: {
								hideTerms: true,
								hideDisclosures: true
							}
						}
					};
					component.applyPortfolioSettingsToPage();
					expect(component.allowFuturePayment).toEqual(true);
					expect(component.allowPayToday).toEqual(true);
				});
				it('component paymentDate should set to current Date when portalSettings paymentTypes allowSingle is true', () => {
					const businessTimezone = 'US/Pacific';
					const businessPaymentDate = moment.tz(businessTimezone).format('MM/DD/YYYY');

					component.model.paymentDate = '';
					stubState.business = {
						portalSettings: {
							paymentTypes: {
								maxFutureDays: ''
							},
							ezpay: {
								paymentTypes: {
									allowFuture: true,
									allowSingle: true
								},
								accountNumberHandling: ''
							},
							businessSettings: {
								hideTerms: true,
								hideDisclosures: true
							}
						}
					};
					stubState.business.businessTimezone = businessTimezone;
					component.applyPortfolioSettingsToPage();
					expect(component.model.paymentDate).toEqual(businessPaymentDate);
				});
			});
			describe('submitTodayPayment success case', () => {
				it('should call payrazrService submitCardPaymentV2 when component paymentInformation card has a value', async () => {
					stubState.business = {
						portalSettings: {
							fields: {
								billingEmail: {
									visible: true
								}
							}
						}
					};
					when(mockPayrazrService.submitCardPaymentV2(
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything()))
						.thenResolve({
							response: {
								isSuccessful: true,

							}
                    });
                    component.account = {
                        bucketKey: null
                    }
					component.paymentInformation = {
						card: '4444333322221111',
						isEmailOptedIn: true,
						name: 'Tom Smith',
						addressLine1: '123 address 1',
						billingCity: 'Phoenix',
						billingState: 'AZ',
						zipCode: '12345',
						billingEmail: 'test@unitTest.com',
						transactionDescription: '',
						isThirdPartyPayment: false,
						fee: '',
						amount: 100
					};
					component.submitTodayPayment(anything());
					verify(mockPayrazrService.submitCardPaymentV2(
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
                        anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything()
					)).called();
				});

				it('should call payrazrService submitDigitalWalletPayment when component paymentInformation digitalWallet has a value', async () => {
					// Arrange
					stubState.business = {
						portalSettings: {
							fields: {
								billingEmail: {
									visible: true
								}
							}
						}
					};
					when(mockPayrazrService.submitCardDigitalWalletPayment(anything())).thenResolve({
						response: {
							isSuccessful: true,
						}
					});
					spyOn(component, 'getDigitalWalletPaymentDetails').and.returnValue(anything());
					component.account = {
						bucketKey: null
					};
					component.paymentInformation = {
						card: '',
						digitalWalletSettings: anything(),
						isEmailOptedIn: true,
						name: 'Tom Smith',
						addressLine1: '123 address 1',
						billingCity: 'Phoenix',
						billingState: 'AZ',
						zipCode: '12345',
						billingEmail: 'test@unitTest.com',
						transactionDescription: '',
						isThirdPartyPayment: false,
						fee: '',
						amount: 100
					};

					// Act
					component.submitTodayPayment(anything());

					// Assert
					verify(mockPayrazrService.submitCardDigitalWalletPayment(anything()))
						.called();
				});

				it('should complete Apple Pay session when submitTodayPayment fails', async () => {
					// Arrange
					const mockError = new Error();
					const mockApplePaySettings = mock(ApplePaySettings);
					mockApplePaySettings.isCard = () => true;
					mockApplePaySettings.isApplePay = () => true;

					component.isDigitalWalletPayment = true;

					stubState.business = {
						portalSettings: {
							fields: {
								billingEmail: {
									visible: true
								}
							}
						}
					};

					when(mockPayrazrService.submitCardDigitalWalletPayment(anything()))
						.thenReject(mockError);

					spyOn(component, 'getDigitalWalletPaymentDetails')
						.and
						.returnValue(anything());

					spyOn(ApplePayUtil, 'completeSession');

					component.account = {
						bucketKey: null
					};
					component.paymentInformation = {
						card: '',
						digitalWalletSettings: mockApplePaySettings,
						isEmailOptedIn: true,
						name: 'Tom Smith',
						addressLine1: '123 address 1',
						billingCity: 'Phoenix',
						billingState: 'AZ',
						zipCode: '12345',
						billingEmail: 'test@unitTest.com',
						transactionDescription: '',
						isThirdPartyPayment: false,
						fee: '',
						amount: 100
					};

					// Act
					await component.submitTodayPayment(anything());

					// Assert
					verify(mockPayrazrService.submitCardDigitalWalletPayment(anything()))
						.called();
					expect(ApplePayUtil.completeSession)
						.toHaveBeenCalledTimes(1);
					expect(component.isSubmitting)
						.toBeFalse();
					expect(component.transactionError)
						.toEqual(mockError);
				});

				it('should call payrazrService submitCheckPaymentV2 when component paymentInformation card has no value', async () => {
					stubState.business = {
						portalSettings: {
							fields: {
								billingEmail: {
									visible: true
								}
							}
						}
					};
					when(mockPayrazrService.submitCheckPaymentV2(
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything()))
						.thenResolve({
							response: {
								isSuccessful: true,

							}
                    });
                    component.account = {
                        bucketKey: null
                    }
					component.paymentInformation = {
						card: '',
						isEmailOptedIn: true,
						name: 'Tom Smith',
						addressLine1: '123 address 1',
						billingCity: 'Phoenix',
						billingState: 'AZ',
						zipCode: '12345',
						billingEmail: 'test@unitTest.com',
						transactionDescription: '',
						isThirdPartyPayment: false,
						fee: '',
						amount: 100
					};
					component.submitTodayPayment(anything());
					verify(mockPayrazrService.submitCheckPaymentV2(
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
						anything(),
                        anything(),
						anything(),
						anything(),
						anything()
					)).called();
				});
			});

			describe('submitFuturePayment success case', () => {
				it('should call payrazrService submitDigitalWalletPaymentPlan when component paymentInformation digital wallet has a value', async () => {
					// Arrange
					stubState.business = {
						portalSettings: {
							fields: {
								billingEmail: {
									visible: true
								}
							}
						}
					};

					component.isDigitalWalletPayment = true;

					component.account = {
						bucketKey: null
					};

					const mockApplePaySettings = mock(ApplePaySettings);
					mockApplePaySettings.isCard = () => true;
					mockApplePaySettings.isApplePay = () => true;

					component.paymentInformation = {
						card: '',
						digitalWalletSettings: mockApplePaySettings,
						isEmailOptedIn: true,
						name: 'Tom Smith',
						addressLine1: '123 address 1',
						billingCity: 'Phoenix',
						billingState: 'AZ',
						zipCode: '12345',
						billingEmail: 'test@unitTest.com',
						transactionDescription: '',
						isThirdPartyPayment: false,
						fee: '',
						amount: 100
					};

					spyOn(component.payrazrService, 'submitDigitalWalletPaymentPlan')
						.and
						.resolveTo({response: { isSuccessful: true }});

					spyOn(component, 'getDigitalWalletPaymentPlanDetailsFromSubmitEvent')
						.and
						.returnValue(anything());
					spyOn(ApplePayUtil, 'completeSession');

					// Act
					await component.submitFuturePayment(anything());
					
					// Assert
					expect(component.payrazrService.submitDigitalWalletPaymentPlan)
						.toHaveBeenCalledTimes(1);
				});
			});

			describe('look me up', () => {
				it('should call lookupCustomerByFilters with success response', () => {
					stubState.business = {
						portalSettings: {
							ezpay: {
								registrationIdSettings: false,
								registrationId2Settings: false,
								registrationId3Settings: false,
								zipcodeSettings: false,
								accountNumberSettings : [
									{
										required: false
									}
								]
							}
						}
					};

					when(mockPayrazrService.lookupCustomerByFilters(anything(), anything(), anything(), anything(), anything(), anything())).thenResolve({
						response: {
							isSuccessful: true,
						}
                    });
					component.search();
					verify(mockPayrazrService.lookupCustomerByFilters(anything(), anything(), anything(), anything(), anything(), anything())).called();
				});
			});

			describe('handleSelectMerchantChanged', () => {
				it('should apply portfolio based on merchant selected', () => {
					stubState.business = {
						portalSettings: {
							ezpay: {
								registrationIdSettings: false,
								registrationId2Settings: false,
								registrationId3Settings: false,
								zipcodeSettings: false,
								accountNumberSettings : [
									{
										required: false
									}
								]
							}
						},
						applyPortfolioSettingsToPortal: () => {}
					};

					component.account = {
                        portfolioName: 'ezPay'
                    }

					component.selectedMerchant = instance(mockMerchant);
					component.selectedMerchant.merchantKey = 1;

					spyOn(stubState.business, 'applyPortfolioSettingsToPortal').and.stub();
					spyOn(component, 'applyPortfolioSettingsToPage').and.stub();
					spyOn(component, 'applyMerchantSettingsToPage').withArgs(component.account.portfolioName).and.stub();
					spyOn(component, 'publishEventsAfterMerchantSelected').and.stub();

					component.handleSelectMerchantChanged(anything(), anything());

					expect(component.applyPortfolioSettingsToPage).toHaveBeenCalled();
					expect(component.applyMerchantSettingsToPage).toHaveBeenCalled();
					expect(component.publishEventsAfterMerchantSelected).toHaveBeenCalled();

				});
			});

		});

	});
});
